import { inject, Injectable } from '@angular/core';
import { TosDocumentModel, UserModel } from '@libs/account/models';
import { AuthService } from '@libs/shared/auth';
import { SelfConfigModel } from '@libs/shared/models';
import { BehaviorSubject, filter, firstValueFrom, map, Observable, switchMap, take, tap } from 'rxjs';
import { SelfConfigRepositoryService } from './self-config-repository.service';
import { UserRepositoryService } from './user-repository.service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly user$: BehaviorSubject<UserModel | undefined> = new BehaviorSubject<UserModel | undefined>(undefined);
  private readonly passwordChangeUrl$: BehaviorSubject<string | undefined> = new BehaviorSubject<string | undefined>(undefined);

  private readonly userRepository: UserRepositoryService = inject(UserRepositoryService);
  private readonly selfConfigRepository: SelfConfigRepositoryService = inject(SelfConfigRepositoryService);
  private readonly authService: AuthService = inject(AuthService);

  public userLoaded(): boolean {
    return !!this.user$.getValue();
  }

  public passwordChangeUrlLoaded(): boolean {
    return !!this.passwordChangeUrl$.getValue();
  }

  public getUser(): Observable<UserModel | undefined> {
    return this.user$.asObservable();
  }

  public getPasswordChangeUrl(): Observable<string | undefined> {
    return this.passwordChangeUrl$.asObservable();
  }

  public getCurrentUser(forceReload: boolean = false): Observable<UserModel | undefined> {
    if (this.userLoaded() && !forceReload) {
      return this.user$.asObservable();
    }

    return this.authService.isUserAuthorized$('user')
      .pipe(
        filter(isAuthenticated => isAuthenticated),
        take(1),
        switchMap(() => {
          return this.userRepository.read().pipe(
            tap(user => void this.user$.next(user)),
            switchMap(() => this.user$.asObservable()),
          );
        }));
  }

  public loadUser(): Promise<UserModel | undefined> {
    return firstValueFrom(this.userRepository.read().pipe(tap((value: UserModel | undefined) => void this.user$.next(value))));
  }

  public loadPasswordUrl(): Promise<string | undefined> {
    return firstValueFrom(this.selfConfigRepository.read().pipe(
      map((config?: SelfConfigModel) => config?.passwordChangeUrl),
      tap((passwordUrl: string | undefined) => void this.passwordChangeUrl$.next(passwordUrl)),
    ));
  }

  public editUser(data: Partial<UserModel>): Observable<UserModel | undefined> {
    return this.userRepository.update(data).pipe(
      tap((user?: UserModel) => void this.user$.next(user)),
    );
  }

  public deleteUser(data: Partial<UserModel>): Observable<UserModel | undefined> {
    return this.userRepository.delete(data).pipe(
      tap((user?: UserModel) => void this.user$.next(user)),
    );
  }

  public checkTos(appId: string): Observable<TosDocumentModel | undefined> {
    return this.userRepository.checkTos(appId);
  }

  public currentTos(appId: string): Observable<TosDocumentModel | undefined> {
    return this.userRepository.currentTos(appId);
  }

  public consentTos(tosId: string): Observable<void> {
    return this.userRepository.consentTos(tosId);
  }
}
